import { schedule as scheduleApi } from '@cwos/api-client';
import type { schedule as SCH } from '@cwos/api-client';
import type { PersonDirectory } from '../personDirectory.js';

/**
 * READING A CANCELLATION — the vocabulary, and the four states, in one place.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS A MODULE AND NOT JSX
 * ---------------------------------------------------------------------------
 * `coverageLogic.ts`'s own note explains why the cancellation vocabulary was
 * drafted and then deleted: "there is nowhere to get the data", because
 * `GET /schedule/cancellations/:id` takes a cancellation id and no surface had
 * one. `GET /schedule/shifts/:id/cancellation` now exists, so the data has
 * somewhere to come from and the vocabulary comes back — as pure functions,
 * because the interesting decisions here are all about which of several
 * near-identical facts a sentence is allowed to claim, and those are testable
 * without a browser.
 *
 * ---------------------------------------------------------------------------
 * THE COLLAPSE THIS FILE EXISTS TO PREVENT
 * ---------------------------------------------------------------------------
 * An UNANSWERED ask and an ANSWERED one must not render the same. Neither must
 * an ask that was never put. A cancellation carries THREE questions, each with
 * its own absence, and a screen that reads `reasonCategory === null` as "we
 * don't know why" answers all three at once and gets two of them wrong:
 *
 *   1. WHOSE SIDE was it? — always present (`attributedTo`, which the schema
 *      requires), so there is no absent state here at all. `'unknown'` is a
 *      CHOSEN member of the vocabulary — a scheduler saying "I do not know" —
 *      and is not the same as nobody having recorded anything.
 *   2. WHICH ACCOUNT did it? — `cancelledByUserId`, null when the act carried
 *      no user. UNATTRIBUTED, and said as that.
 *   3. WHY? — three absences behind one null: nobody was asked (`askedAt` is
 *      null), somebody was asked and has not answered, or somebody was asked,
 *      did not answer, and it has gone to their manager.
 *
 * The server decides (3) as `askState` and this module never re-derives it: the
 * `overdue`/`escalated` boundary is a comparison against `now()`, and the server
 * makes it in the same clock the escalation sweep uses.
 */

export type AskState = SCH.CancellationAskState;

/**
 * The heading for each position, as a manager reads it.
 *
 * A `Record` over the closed union rather than a `switch` with a default, so a
 * sixth state added to the server's vocabulary fails this file's typecheck
 * instead of falling into a catch-all sentence that would be wrong for it.
 */
export const ASK_STATE_READING: Readonly<Record<AskState, string>> = Object.freeze({
  not_asked: 'Nobody was asked',
  awaiting: 'Waiting for an answer',
  overdue: 'No answer, past the deadline',
  escalated: 'Escalated to a manager',
  answered: 'Answered',
});

/**
 * The sentence UNDER the heading — what this state means and what it does not.
 *
 * `not_asked` gets the longest one on purpose. It is the state most easily
 * misread as a failure, and it is usually the opposite: a client cancellation
 * raises no question for the worker, because the worker has nothing to explain.
 */
export const ASK_STATE_EXPLANATION: Readonly<Record<AskState, string>> = Object.freeze({
  not_asked:
    'No question was put to anybody. Either the reason was recorded at the time, or this was not the worker’s cancellation to explain — a client cancellation is not staff underperformance and raises no question. This is not an unanswered ask.',
  awaiting: 'The staff member has been asked once and still has time to answer.',
  overdue:
    'The deadline has passed with no answer. Phibian asks once; what follows is the manager, not another message.',
  escalated:
    'The question went unanswered and has been handed to the manager the org chart names. It is still unanswered — escalating is not answering.',
  answered: 'A reason is on the record. A reason is given once and is not overwritten.',
});

export type AskTone = 'good' | 'look' | 'urgent' | 'unsure' | 'plain';

export function askStateTone(state: AskState): AskTone {
  switch (state) {
    case 'answered':
      return 'good';
    case 'awaiting':
      return 'unsure';
    case 'overdue':
      return 'look';
    case 'escalated':
      return 'urgent';
    case 'not_asked':
      // PLAIN, not a warning tone. Nothing is owed here and colouring it as
      // though something were would send managers to chase nothing.
      return 'plain';
  }
}

/** The order a manager works down. Overdue and escalated first; both are owed. */
export const ASK_STATE_PRIORITY: readonly AskState[] = Object.freeze([
  'escalated',
  'overdue',
  'awaiting',
  'answered',
  'not_asked',
]);

/** The two states the ask queue exists for: somebody owes an action right now. */
export const OUTSTANDING_ASK_STATES: readonly AskState[] = Object.freeze(['overdue', 'escalated']);

/** Whose side, in words. Keyed off the client's own exported vocabulary. */
export const ATTRIBUTION_LABEL: Readonly<Record<SCH.Attribution, string>> = Object.freeze({
  worker: 'The worker',
  client: 'The client',
  organization: 'This organization',
  external: 'Something outside',
  unknown: 'Not known',
});

/**
 * What each attribution COMMITS the record to, shown beside the choice.
 *
 * The `worker` entry says out loud that choosing it puts a question to a person,
 * because that is the consequence a scheduler most needs to know before
 * clicking: the vocabulary is not cosmetic, and 'worker' is the only member that
 * raises a task addressed to somebody.
 */
export const ATTRIBUTION_CONSEQUENCE: Readonly<Record<SCH.Attribution, string>> = Object.freeze({
  worker:
    'Asks the staff member once, with a deadline, for a reason. Not a mark against them — the schedule needs covering.',
  client: 'Asks the staff member nothing. The client still needs the visit rescheduled.',
  organization: 'Asks the staff member nothing. Recorded as our own cancellation.',
  external: 'Asks the staff member nothing. Weather, a closure, something neither side controlled.',
  unknown:
    'Recorded as genuinely not known. Choose this rather than guessing — it is a different fact from “nobody said”.',
});

export const REASON_LABEL: Readonly<Record<string, string>> = Object.freeze({
  illness: 'Illness',
  family: 'Family',
  transport: 'Transport',
  weather: 'Weather',
  scheduling_conflict: 'Scheduling conflict',
  client_unavailable: 'Client unavailable',
  client_declined: 'Client declined',
  safety: 'Safety',
  other: 'Other',
});

/** The label for a stored reason, or the raw key when the server sent a newer one. */
export function reasonReading(category: string | null): string | null {
  if (category === null) return null;
  return REASON_LABEL[category] ?? category;
}

/**
 * WHO CANCELLED IT — the acting account, never the attributed side.
 *
 * Returns `null` when `cancelledByUserId` is null, and the CALLER is expected to
 * render that as "unattributed" rather than omitting the row. An omitted row
 * reads as "we did not think to show this"; the point of the full door is that
 * an unattributed cancellation is a visible fact about the record.
 */
export function cancelledByReading(
  view: Pick<SCH.CancellationView, 'cancelledByUserId'>,
  people: PersonDirectory,
): string | null {
  return view.cancelledByUserId === null ? null : people.name(view.cancelledByUserId);
}

/**
 * WHO ANSWERED, and whether that was the worker or somebody on their behalf.
 *
 * `subjectUserId` is the `app_user.id` of the worker the shift belonged to, when
 * the surface knows it. When it does not, the answer is still attributed by name
 * and the `onBehalf` flag is `null` — UNDETERMINED, not `false`. Reporting
 * `false` would be the screen claiming the worker answered when it has no way to
 * know, which is the same class of false claim as rendering a withheld value as
 * a zero.
 */
export interface AnswerAttribution {
  answeredByName: string;
  /** True when the answerer is demonstrably not the worker; null when unknown. */
  onBehalf: boolean | null;
}

export function answerAttribution(
  view: Pick<SCH.CancellationView, 'reasonGivenByUserId'>,
  people: PersonDirectory,
  subjectUserId: string | null | undefined,
): AnswerAttribution | null {
  if (view.reasonGivenByUserId === null) return null;
  return {
    answeredByName: people.name(view.reasonGivenByUserId),
    onBehalf:
      subjectUserId === null || subjectUserId === undefined
        ? null
        : view.reasonGivenByUserId !== subjectUserId,
  };
}

/**
 * THE RESCHEDULING OBLIGATION — four answers, and only one of them is "fine".
 *
 * The operational signal in this whole family is not the call-out. It is the
 * failure to put the client's visit back on the calendar, which is about an
 * obligation nobody discharged rather than a bad day somebody had. So this is
 * reported separately from the ask and never folded into it.
 */
export type RescheduleStanding =
  /** No replacement was required — a decision recorded at cancellation time. */
  | { kind: 'not_required' }
  /** Required, and a replacement visit exists. */
  | { kind: 'replaced'; replacementShiftId: string }
  /** Required, no replacement yet, and a task is tracking it. */
  | { kind: 'open'; taskId: string }
  /**
   * Required, no replacement, and NO TASK EITHER.
   *
   * A real degraded state, not a rendering artefact: `cancelShift` commits the
   * cancellation before the tasks domain opens its own transaction, so a failure
   * in the second half leaves `reschedule_required = true` with a null task id.
   * The sweep still finds it — that is the shape
   * `shift_cancellation_unrescheduled_idx` exists for — but a screen that drew
   * this identically to `open` would tell a manager work is being tracked when
   * nothing is tracking it.
   */
  | { kind: 'untracked' };

export function rescheduleStanding(
  view: Pick<
    SCH.CancellationView,
    'rescheduleRequired' | 'rescheduledShiftId' | 'rescheduleTaskId'
  >,
): RescheduleStanding {
  if (view.rescheduledShiftId !== null) {
    return { kind: 'replaced', replacementShiftId: view.rescheduledShiftId };
  }
  if (!view.rescheduleRequired) return { kind: 'not_required' };
  return view.rescheduleTaskId === null
    ? { kind: 'untracked' }
    : { kind: 'open', taskId: view.rescheduleTaskId };
}

export const RESCHEDULE_READING: Readonly<Record<RescheduleStanding['kind'], string>> =
  Object.freeze({
    not_required:
      'No replacement visit was required. That was recorded when the shift was called off, not inferred from the absence of one.',
    replaced: 'A replacement visit is on the calendar.',
    open: 'The client still needs this visit. An open task is tracking it.',
    untracked:
      'The client still needs this visit and NO task is tracking it. The obligation stands; it is being found by the sweep rather than by an assignee.',
  });

/**
 * Group a list of cancellations by state, in the order a manager works down.
 *
 * Returns every group the list actually contains, in `ASK_STATE_PRIORITY` order,
 * and NEVER synthesises an empty group: an empty section under a heading reads
 * as a measured zero for that state, and a filtered list has not measured the
 * states it filtered out.
 */
export function groupByAskState(
  entries: readonly SCH.CancellationListEntry[],
): Array<{ state: AskState; entries: SCH.CancellationListEntry[] }> {
  const buckets = new Map<AskState, SCH.CancellationListEntry[]>();
  for (const entry of entries) {
    const list = buckets.get(entry.askState);
    if (list === undefined) buckets.set(entry.askState, [entry]);
    else list.push(entry);
  }
  return ASK_STATE_PRIORITY.filter((s) => buckets.has(s)).map((state) => ({
    state,
    entries: buckets.get(state)!,
  }));
}

/**
 * The pickable attribution options, in the order they are offered.
 *
 * Derived from the client's exported vocabulary, never retyped: a member added
 * to `CANCELLATION_ATTRIBUTIONS` appears here without an edit, and a member
 * removed from it stops being offered. `unknown` is moved to the END rather than
 * dropped — it is a legitimate answer and hiding it would push a scheduler who
 * genuinely does not know into picking one that is wrong.
 */
export const ATTRIBUTION_OPTIONS: readonly SCH.Attribution[] = Object.freeze(
  [...scheduleApi.CANCELLATION_ATTRIBUTIONS].sort((a, b) =>
    a === 'unknown' ? 1 : b === 'unknown' ? -1 : 0,
  ),
);

/** Guards the state list this module's records claim to cover. */
export const KNOWN_ASK_STATES: readonly AskState[] = scheduleApi.CANCELLATION_ASK_STATES;
